/**
 * KOA-18593 — A LINE OF A STOPPED SCRIPT THAT THE PROCESS ALREADY HOLDS IS NOT A LOST LINE.
 *
 * ## The defect
 *
 * Live `202cd605`. A process script stopped on a broken canvas, and the halt gate then held
 * every later script to that script's text: any durable line the next script did not repeat was
 * "dropped", and the script was refused. The author did what its teaching says after a stop — read
 * the process with `LIST` and send only what is missing — and five scripts in a row were refused,
 * among them every script of the repair round. The gate compared TEXT with TEXT and never looked at
 * the process: a line whose work already stood on the diagram counted as lost exactly like one whose
 * work was gone.
 *
 * ## What this answers
 *
 * For each durable line of the stopped script: does the process, as `LIST` shows it, already hold
 * what that line builds? The answer is read off the SAME listing the author reads, so the gate and
 * the author judge "what is still missing" from one picture. A line whose kind this cannot read is
 * never forgiven — it stays a lost line, which is exactly what the gate did before, so this can turn
 * a refusal into an acceptance only where the process demonstrably holds the line's work.
 */
import type { IStepSummary, IWfParamSummary } from "../../designer-workflow-step-actions";
import type { DslCommand, GatewayCmd, ParseResult } from "./workflow-dsl-parser";
import { parseDsl } from "./workflow-dsl-parser";

/** The process as `LIST` shows it. */
export interface IStandingSnapshot {
  readonly steps: readonly IStepSummary[];
  readonly parameters: readonly IWfParamSummary[];
}

/** What an `@alias` of the stopped script was created as. */
interface IAliasedNode {
  readonly type: string;
  readonly name: string;
}

const GATEWAY_TYPES: Record<GatewayCmd["gatewayType"], string> = {
  Exclusive: "bpmn:ExclusiveGateway",
  Inclusive: "bpmn:InclusiveGateway",
  Parallel: "bpmn:ParallelGateway"
};

function sameName(a: string | undefined, b: string | undefined): boolean {
  return (a ?? "").trim() === (b ?? "").trim() && (a ?? "").trim().length > 0;
}

function sameNameIgnoringCase(a: string | undefined, b: string | undefined): boolean {
  return (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase() && (a ?? "").trim().length > 0;
}

/** The node each `@alias` names, from the stopped script's own creation lines. */
function aliasesOf(commands: readonly DslCommand[]): ReadonlyMap<string, IAliasedNode> {
  const out: Map<string, IAliasedNode> = new Map<string, IAliasedNode>();
  for (const command of commands) {
    if (command.kind === "TASK") out.set(command.alias, { type: "bpmn:UserTask", name: command.name });
    else if (command.kind === "SYSTEM") out.set(command.alias, { type: "bpmn:Task", name: command.name });
    else if (command.kind === "LANE") out.set(command.alias, { type: "bpmn:Lane", name: command.name });
    else if (command.kind === "GATEWAY" && command.name !== undefined) {
      out.set(command.alias, { type: GATEWAY_TYPES[command.gatewayType], name: command.name });
    }
  }
  return out;
}

/** The element a reference of the stopped script names on the process, or undefined. */
function resolveNode(
  ref: string,
  aliases: ReadonlyMap<string, IAliasedNode>,
  snapshot: IStandingSnapshot
): IStepSummary | undefined {
  const trimmed: string = ref.trim();
  if (trimmed.startsWith("@")) {
    const aliased: IAliasedNode | undefined = aliases.get(trimmed);
    if (aliased === undefined) return undefined;
    return snapshot.steps.find(
      (step: IStepSummary): boolean => step.type === aliased.type && sameName(step.name, aliased.name)
    );
  }
  if (trimmed === "StartEvent_1")
    return snapshot.steps.find((step: IStepSummary): boolean => step.type === "bpmn:StartEvent");
  if (trimmed === "EndEvent_1")
    return snapshot.steps.find((step: IStepSummary): boolean => step.type === "bpmn:EndEvent");
  return snapshot.steps.find((step: IStepSummary): boolean => step.id === trimmed);
}

/** The path between two nodes, or undefined. */
function flowBetween(
  source: IStepSummary,
  target: IStepSummary,
  snapshot: IStandingSnapshot
): IStepSummary | undefined {
  return snapshot.steps.find(
    (step: IStepSummary): boolean =>
      step.type === "bpmn:SequenceFlow" && step.sourceId === source.id && step.targetId === target.id
  );
}

/** Whether the process already holds what one command of the stopped script builds. */
function stands(command: DslCommand, aliases: ReadonlyMap<string, IAliasedNode>, snapshot: IStandingSnapshot): boolean {
  const node: (ref: string) => IStepSummary | undefined = (ref: string): IStepSummary | undefined =>
    resolveNode(ref, aliases, snapshot);
  switch (command.kind) {
    case "PARAM_BO":
    case "PARAM_INTERNAL":
      return snapshot.parameters.some((parameter: IWfParamSummary): boolean => sameName(parameter.name, command.name));
    case "LANE": {
      const lane: IStepSummary | undefined = snapshot.steps.find(
        (step: IStepSummary): boolean => step.type === "bpmn:Lane" && sameNameIgnoringCase(step.name, command.name)
      );
      if (lane === undefined) return false;
      if (command.roleId === undefined) return true;
      return sameNameIgnoringCase(lane.role?.id, command.roleId);
    }
    case "TASK":
      return snapshot.steps.some(
        (step: IStepSummary): boolean => step.type === "bpmn:UserTask" && sameName(step.name, command.name)
      );
    case "SYSTEM":
      return snapshot.steps.some(
        (step: IStepSummary): boolean => step.type === "bpmn:Task" && sameName(step.name, command.name)
      );
    case "GATEWAY": {
      if (command.name === undefined) return false;
      const type: string = GATEWAY_TYPES[command.gatewayType];
      return snapshot.steps.some(
        (step: IStepSummary): boolean => step.type === type && sameName(step.name, command.name)
      );
    }
    case "STEP_ACTION":
      return (node(command.stepId)?.actions ?? []).some((action): boolean =>
        sameNameIgnoringCase(action.name, command.actionName)
      );
    case "MAP_INPUT":
      return (node(command.stepId)?.actions ?? []).some((action): boolean =>
        action.mappedInputs.some((input: string): boolean => sameName(input, command.parameterName))
      );
    case "MAP_OUTPUT": {
      const produced: readonly string[] = node(command.nodeId)?.outputParameterNames ?? [];
      const byId: IWfParamSummary | undefined = snapshot.parameters.find(
        (parameter: IWfParamSummary): boolean => parameter.id === command.parameterId
      );
      const name: string = byId?.name ?? command.parameterId;
      return produced.some((output: string): boolean => sameName(output, name));
    }
    case "INTERACTION": {
      const task: IStepSummary | undefined = node(command.taskId);
      if (task === undefined) return false;
      if (!(task.interactionLabels ?? []).some((label: string): boolean => sameName(label, command.label)))
        return false;
      const assigned: readonly string[] = task.interactionSets ?? [];
      return command.parameters.every((set): boolean =>
        assigned.some((name: string): boolean => sameName(name, set.name))
      );
    }
    case "PATH": {
      const source: IStepSummary | undefined = node(command.sourceNodeId);
      const target: IStepSummary | undefined = node(command.targetNodeId);
      if (source === undefined || target === undefined) return false;
      const flow: IStepSummary | undefined = flowBetween(source, target, snapshot);
      if (flow === undefined) return false;
      if (command.isDefault && flow.isDefault !== true) return false;
      if ((command.conditions ?? []).length > 0 && (flow.condition ?? "").length === 0) return false;
      return true;
    }
    case "CONNECT": {
      const source: IStepSummary | undefined = node(command.sourceNodeId);
      const target: IStepSummary | undefined = node(command.targetNodeId);
      if (source === undefined || target === undefined) return false;
      return flowBetween(source, target, snapshot) !== undefined;
    }
    case "DELETE":
      // A literal id that is gone from the process: its delete has nothing left to do.
      return !command.elementId.trim().startsWith("@") && node(command.elementId) === undefined;
    case "LAYOUT":
      // Arranges what stands and builds nothing, so there is nothing of it to lose.
      return true;
    default:
      return false;
  }
}

/**
 * The durable lines of a stopped script whose work the process already holds.
 *
 * `lines` are the stopped script's durable lines in its own order, so the alias of every node is
 * read from the script that created it. Returns the subset that stands, as the same strings.
 */
export function standingDurableLines(lines: readonly string[], snapshot: IStandingSnapshot): ReadonlySet<string> {
  const parsed: ParseResult = parseDsl(lines.join("\n"));
  const aliases: ReadonlyMap<string, IAliasedNode> = aliasesOf(parsed.commands);
  const out: Set<string> = new Set<string>();
  for (const command of parsed.commands) {
    const line: string | undefined = lines[command.line - 1];
    if (line === undefined) continue;
    if (stands(command, aliases, snapshot)) out.add(line);
  }
  return out;
}

/** The element each path or connection of a script leaves from, as the process shows it. */
function connectionSources(
  commands: readonly DslCommand[],
  aliases: ReadonlyMap<string, IAliasedNode>,
  snapshot: IStandingSnapshot
): Map<number, string> {
  const out: Map<number, string> = new Map<number, string>();
  for (const command of commands) {
    if (command.kind !== "PATH" && command.kind !== "CONNECT") continue;
    const source: IStepSummary | undefined = resolveNode(command.sourceNodeId, aliases, snapshot);
    if (source !== undefined) out.set(command.line, source.id);
  }
  return out;
}

/**
 * KOA-18593 — the paths and connections of a stopped script that the next script REDRAWS.
 *
 * Live `202cd605`, the repair round: the builder redrew the budget decision's paths the way the
 * spec has them — two new paths out of the same decision, reached by its id on the process — and
 * left out the stopped script's "Bütçe Uygun Değil → end". The halt gate refused it for dropping
 * that line, i.e. it demanded back the very connection the correction replaced. Neither text nor
 * label can tell a replaced path from a lost one (both changed here). What can: the next script
 * draws paths out of the SAME element, found on the process by alias or by id. The paths out of an
 * element the next script draws from are then that script's to say; one it leaves out is not lost
 * work but a choice, and a branch really missing is the lint's and the completion check's to name.
 */
export function supersededConnectionLines(
  halted: readonly string[],
  next: readonly string[],
  snapshot: IStandingSnapshot
): ReadonlySet<string> {
  const haltedParsed: ParseResult = parseDsl(halted.join("\n"));
  const nextParsed: ParseResult = parseDsl(next.join("\n"));
  const redrawn: Set<string> = new Set<string>(
    connectionSources(nextParsed.commands, aliasesOf(nextParsed.commands), snapshot).values()
  );
  const out: Set<string> = new Set<string>();
  if (redrawn.size === 0) return out;
  for (const [line, sourceId] of connectionSources(haltedParsed.commands, aliasesOf(haltedParsed.commands), snapshot)) {
    const text: string | undefined = halted[line - 1];
    if (text !== undefined && redrawn.has(sourceId)) out.add(text);
  }
  return out;
}
